/**
 * <mb-support-launcher> — our own launcher for the Freshchat support widget,
 * and the only thing that loads Freshchat at all.
 * ─────────────────────────────────────────────────────────────────────────────
 * Two jobs, and the second is the reason this file exists.
 *
 * 1 · REPLACE THE STOCK PILL. Freshchat renders its launcher inside a
 *     cross-origin iframe (#fc_widget, inside #fc_frame), so its yellow
 *     "? Support" button cannot be restyled with CSS — not by a selector, not
 *     by a cascade trick. The only way to control that surface is to stop using
 *     it: modern-bold.css hides the collapsed frame and we drive the widget
 *     through its public API instead.
 *
 * 2 · KEEP IT OFF THE CRITICAL PATH. The SDK costs 727KB over the wire across
 *     23 requests and four hosts, and unpacks to roughly 2.8MB of JavaScript to
 *     parse and execute — measured, not estimated. It used to load on `window
 *     load` for every visitor on every page; almost none of them open a chat.
 *
 * So nothing is fetched until the shopper reaches for the button:
 *
 *   • INTENT — pointerenter / focus / touchstart. Fires before the click
 *     resolves, so the SDK is usually in flight (or done) by the time they
 *     actually press. Costs nothing if they never press.
 *   • PRESS — the click itself, for anyone who gets there first. The seal spins
 *     while we wait and the panel opens by itself once the widget is ready.
 *
 * There is deliberately no timer, and the reason is worth writing down because
 * the dashboard and the config disagree.
 *
 * Freshchat's config advertises a proactive rule — "Design Campaign", fired on
 * TIME_ON_CURRENT_PAGE > 60, marked `enabled: true`. It does not deliver: the
 * Topic it sends into has been disabled, so Freshchat flags it in the dashboard
 * ("Topic mapped to this campaign is disabled") and drops it. Measured: 220s on
 * a loaded widget produced nothing. So preloading the SDK to keep it alive, as
 * an earlier revision of this file did, was buying nothing.
 *
 * That rule is dormant rather than retired — it had sent 3M messages for 15.9K
 * replies before the Topic broke. If anyone re-points it at a live Topic, this
 * file has to change too, or the revived campaign still never fires: the SDK
 * must already be running when its threshold passes. Restore a preload that
 * boots EARLY, well under the 60s mark, since the widget counts from its own
 * boot rather than from navigation.
 *
 * Note that nothing here mirrors unread replies onto the button. The stock pill
 * showed a count; ours cannot, because with no SDK loaded there is nothing to
 * ask. Surfacing that again would mean loading on a timer after all, plus an
 * `unreadCount:notify` subscription.
 *
 * If the SDK cannot be fetched at all, the button stops pretending: it sends
 * the shopper to the contact page rather than acting as a control that does
 * nothing. The old build could fall back on Freshchat's own pill; there is no
 * pill to fall back on when nothing has loaded.
 */

if (!customElements.get('mb-support-launcher')) {
  class MBSupportLauncher extends HTMLElement {
    /** Give up on a wedged SDK rather than spin at the shopper forever. */
    static LOAD_TIMEOUT_MS = 15000;

    #cleanups = [];
    #button = null;
    #status = null;
    #sdk = null;

    connectedCallback() {
      this.#button = this.querySelector('[data-mb-support-button]');
      this.#status = this.querySelector('[data-mb-support-status]');
      if (!this.#button) return;

      this.#on(this.#button, 'click', () => this.#openChat());

      // Warm the SDK on the first sign the shopper is heading for the button.
      // once:true — the loader dedupes anyway, but there is no reason to keep
      // re-firing on every hover.
      for (const type of ['pointerenter', 'focus', 'touchstart']) {
        this.#on(this.#button, type, () => this.#loadSdk().catch(() => {}), {
          once: true,
          passive: true,
        });
      }
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    #on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.#cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    #say(message) {
      if (this.#status) this.#status.textContent = message;
    }

    /**
     * Fetches and initialises the SDK exactly once. Repeat callers — a hover
     * followed by a click followed by the timer — all await the same promise.
     */
    #loadSdk() {
      if (this.#sdk) return this.#sdk;

      const { fcToken: token, fcHost: host, fcWidgetUuid: widgetUuid } = this.dataset;

      this.#sdk = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = 'Freshchat-js-sdk';
        script.async = true;
        script.src = `${host}/js/widget.js`;

        const bail = setTimeout(() => reject(new Error('Freshchat SDK timed out')), MBSupportLauncher.LOAD_TIMEOUT_MS);

        script.onload = () => {
          clearTimeout(bail);
          if (typeof window.fcWidget?.init !== 'function') {
            reject(new Error('Freshchat SDK loaded without fcWidget'));
            return;
          }
          window.fcWidget.init({
            token,
            host,
            widgetUuid,
            config: {
              // cssNames is Freshchat's documented styling hook: it stamps our
              // own classes onto the widget frame so positioning lives in
              // modern-bold.css instead of fighting #fc_frame's inline styles.
              cssNames: { widget: 'mb-fc-frame', expanded: 'mb-fc-expanded' },
              headerProperty: {
                appName: 'RugOutlet USA',
                appLogo:
                  'https://cdn.shopify.com/s/files/1/0818/4165/1905/files/Screenshot_2026-01-16_at_06.22.01.png?v=1768562538',
              },
            },
          });
          this.#bind(window.fcWidget);
          resolve(window.fcWidget);
        };

        script.onerror = () => {
          clearTimeout(bail);
          reject(new Error('Freshchat SDK failed to load'));
        };

        document.head.appendChild(script);
      });

      // A failed load must not be cached as "in progress" forever — clear it so
      // a later attempt can retry on a recovered connection.
      this.#sdk.catch(() => {
        this.#sdk = null;
      });

      return this.#sdk;
    }

    async #openChat() {
      if (this.hasAttribute('data-mb-loading')) return;

      this.#setLoading(true);
      try {
        const fc = await this.#loadSdk();
        await this.#whenLoaded(fc);
        fc.open();
        this.#say('');
      } catch {
        this.#fallBackToContact();
      } finally {
        this.#setLoading(false);
      }
    }

    /** `widget:loaded` does not replay for late subscribers — check first. */
    #whenLoaded(fc) {
      if (fc.isLoaded?.()) return Promise.resolve();
      return new Promise((resolve) => {
        const done = setTimeout(resolve, MBSupportLauncher.LOAD_TIMEOUT_MS);
        fc.on('widget:loaded', () => {
          clearTimeout(done);
          resolve();
        });
      });
    }

    #setLoading(loading) {
      this.toggleAttribute('data-mb-loading', loading);
      this.#button.setAttribute('aria-busy', String(loading));
      if (loading) this.#say('Connecting to support…');
    }

    /**
     * The SDK is unreachable, so stop offering a chat we cannot open. Swapping
     * in a real <a> keeps the affordance honest — it is a link now, and reads
     * as one to assistive tech too.
     */
    #fallBackToContact() {
      const href = this.dataset.fallbackUrl;
      if (!href) {
        this.#say('Support chat is unavailable right now.');
        return;
      }
      this.#say('Support chat is unavailable. Opening the contact page instead.');
      window.location.href = href;
    }

    #bind(fc) {
      fc.on('widget:opened', () => {
        this.#setOpen(true);
        // The panel lives in a cross-origin iframe, so focusing the frame
        // element is as far in as we can reach; the embedded document takes
        // over from there.
        document.getElementById('fc_widget')?.focus();
      });

      fc.on('widget:closed', () => {
        this.#setOpen(false);
        this.#button.focus();
      });

      // The widget can open itself — a proactive campaign, a restored session —
      // without any event of ours having fired.
      if (fc.isOpen?.()) this.#setOpen(true);
    }

    #setOpen(open) {
      this.toggleAttribute('data-mb-chat-open', open);
      this.#button.setAttribute('aria-expanded', String(open));
    }
  }

  customElements.define('mb-support-launcher', MBSupportLauncher);
}
